/**
 * Minimal ZIP reader — the counterpart of `zip.ts`.
 *
 * Reads stored and deflated entries through the browser's own
 * `DecompressionStream`, which is all an .xlsx needs, and keeps us off a
 * third-party zip dependency.
 */

const EOCD_SIGNATURE = 0x06054b50
const CENTRAL_SIGNATURE = 0x02014b50
const LOCAL_SIGNATURE = 0x04034b50

export interface ZipArchive {
  /** Entry names, as stored in the archive (forward slashes, no leading slash). */
  names: string[]
  /** The entry's bytes, or null when the archive has no such entry. */
  read: (name: string) => Promise<Uint8Array | null>
  /** The entry decoded as UTF-8 text, or null when it is missing. */
  readText: (name: string) => Promise<string | null>
}

interface CentralEntry {
  method: number
  compressedSize: number
  localOffset: number
}

async function inflateRaw(bytes: Uint8Array<ArrayBuffer>): Promise<Uint8Array> {
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'))
  return new Uint8Array(await new Response(stream).arrayBuffer())
}

/** Opens a zip archive. Throws when the file is not one. */
export async function readZip(file: Blob): Promise<ZipArchive> {
  const buffer = await file.arrayBuffer()
  const view = new DataView(buffer)

  // The end-of-central-directory record sits in the last 22 bytes, plus up to
  // 64 KiB of trailing comment.
  let eocd = -1
  for (let i = buffer.byteLength - 22; i >= Math.max(0, buffer.byteLength - 22 - 0xffff); i--) {
    if (view.getUint32(i, true) === EOCD_SIGNATURE) {
      eocd = i
      break
    }
  }
  if (eocd < 0) throw new Error('Not a zip archive')

  const count = view.getUint16(eocd + 10, true)
  let offset = view.getUint32(eocd + 16, true)
  const decoder = new TextDecoder()
  const entries = new Map<string, CentralEntry>()

  for (let i = 0; i < count; i++) {
    if (view.getUint32(offset, true) !== CENTRAL_SIGNATURE) throw new Error('Corrupt zip archive')
    const method = view.getUint16(offset + 10, true)
    const compressedSize = view.getUint32(offset + 20, true)
    const nameLength = view.getUint16(offset + 28, true)
    const extraLength = view.getUint16(offset + 30, true)
    const commentLength = view.getUint16(offset + 32, true)
    const localOffset = view.getUint32(offset + 42, true)
    const name = decoder.decode(new Uint8Array(buffer, offset + 46, nameLength))
    entries.set(name.replace(/^\/+/, ''), { method, compressedSize, localOffset })
    offset += 46 + nameLength + extraLength + commentLength
  }

  const read = async (name: string): Promise<Uint8Array | null> => {
    const entry = entries.get(name)
    if (!entry) return null
    const local = entry.localOffset
    if (view.getUint32(local, true) !== LOCAL_SIGNATURE) throw new Error('Corrupt zip archive')
    // The local header's own name and extra lengths can differ from the central ones
    const start = local + 30 + view.getUint16(local + 26, true) + view.getUint16(local + 28, true)
    const data = new Uint8Array(buffer.slice(start, start + entry.compressedSize))
    if (entry.method === 0) return data
    if (entry.method === 8) return inflateRaw(data)
    throw new Error(`Unsupported zip compression method ${entry.method}`)
  }

  return {
    names: [...entries.keys()],
    read,
    readText: async (name) => {
      const bytes = await read(name)
      return bytes ? decoder.decode(bytes) : null
    },
  }
}
